/**
 * Shared right-side PDF viewer panel (Sheet/drawer anchored right).
 *
 * Displays a PDF in an iframe from a blob object URL, with Download and
 * Open-in-new-tab affordances. Used by InvoicePdfButton across all surfaces
 * (InvoicesPage, PaymentsTab, BROSRecordsList) so the viewer cannot drift.
 *
 * Props:
 *   open         - whether the panel is visible
 *   onClose      - callback when the panel should close
 *   pdfUrl       - the blob object URL to display (always backend-fetched
 *                  via the authenticated API client; never a raw external
 *                  URL -- see InvoicePdfButton.handleView)
 *   filename     - filename for the Download button
 *   loading      - whether the PDF is being fetched
 *   error        - error message string, or null
 *   mediaType    - the artifact's real MIME type (X-Invoice-Media-Type).
 *                  Drive-filed "invoices" are often phone photos, which an
 *                  iframe cannot reliably display; images render as <img>.
 *   renderSource - "xero" | "local" | null. When "local", a reconstruction
 *                  badge is shown. When "xero" or absent, no badge.
 */

import { Loader2, Download, ExternalLink, AlertTriangle, Info } from 'lucide-react';
import { Button } from './ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';

export function PdfViewerPanel({ open, onClose, pdfUrl, filename, loading, error, renderSource, mediaType }) {
  const isImage = typeof mediaType === 'string' && mediaType.startsWith('image/');
  return (
    <Sheet open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <SheetContent
        side="right"
        className="sm:max-w-2xl w-full p-0 flex flex-col"
      >
        <SheetHeader className="pl-6 pr-12 py-4 border-b shrink-0">
          <div className="flex items-center justify-between gap-4">
            <SheetTitle className="truncate text-base">
              {filename || 'Invoice PDF'}
            </SheetTitle>
            {pdfUrl && !loading && !error && (
              <div className="flex gap-2 shrink-0">
                <Button variant="outline" size="sm" asChild>
                  <a href={pdfUrl} download={filename}>
                    <Download className="h-3.5 w-3.5 mr-1.5" />
                    Download
                  </a>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                    New tab
                  </a>
                </Button>
              </div>
            )}
          </div>
          <SheetDescription className="sr-only">Invoice PDF viewer</SheetDescription>
        </SheetHeader>
        {renderSource === 'local' && pdfUrl && !loading && !error && (
          <div
            className="flex items-center gap-2 px-6 py-2 bg-amber-50 border-b text-amber-800 text-xs"
            data-testid="reconstruction-badge"
          >
            <Info className="h-3.5 w-3.5 shrink-0" />
            <span>Reconstruction &ndash; not the issued Xero document</span>
          </div>
        )}
        <div className="flex-1 min-h-0">
          {loading && (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}
          {error && !loading && (
            <div className="flex flex-col items-center justify-center h-full gap-3 px-6 text-center">
              <AlertTriangle className="h-8 w-8 text-amber-500" />
              <p className="text-sm text-muted-foreground max-w-sm">{error}</p>
            </div>
          )}
          {pdfUrl && !loading && !error && isImage && (
            <div className="w-full h-full overflow-auto bg-muted/30 flex items-start justify-center p-4">
              <img
                src={pdfUrl}
                alt={filename || 'Receipt'}
                className="max-w-full h-auto shadow-sm rounded"
                data-testid="receipt-viewer-image"
              />
            </div>
          )}
          {pdfUrl && !loading && !error && !isImage && (
            <iframe
              src={pdfUrl}
              className="w-full h-full border-0"
              title={filename || 'Invoice PDF'}
              data-testid="pdf-viewer-iframe"
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
